import {
  feedSaleStateFromAuctionStatus,
  withLiveFigures,
  type PublicLotRow,
} from '@auctisync/api-client';
import { Button, LearnMoreLink } from '@auctisync/ui';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { LiveStatusBanner } from '../../components/bidder-web/LiveStatusBanner';
import { useLiveSource } from '../../hooks/useLiveSource';
import { useLotSubscription } from '../../hooks/useLotSubscription';
import { useSaleFeed } from '../../hooks/useSaleFeed';
import { readClaims, useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';

interface AuctionRow {
  id: string;
  tenant_id: string;
  title: string;
  status: string;
  timezone: string | null;
}

interface LotRow {
  id: string;
  lot_number: number;
  title: string;
  status: string;
  current_bid_cents: number | null;
  bid_count: number;
}

interface BidderRow {
  user_id: string;
  display_name: string | null;
  tenant_bidder_number: number | null;
}

/**
 * In-browser auctioneer console (Module #11.9). Tenant staff places floor
 * bids on behalf of phone / walk-in bidders without external auctioneer
 * software. Each bid is HMAC-equivalent in chain semantics (source=
 * 'live_webcast_floor') and records the placing staff in the audit trail.
 *
 * 🔴 WHERE THE BID GOES IS THE DATABASE'S ANSWER (2026-10-08). The console
 * posted to a HARD-CODED engine address (the production worker, overridable
 * only at build time) and named the sale by its catalogue id — which the
 * engine refuses: it knows a sale as `auc_main_<sale id>` (router.ts). Now the
 * engine's address and its id for the sale come from the bid path
 * (`useLiveSource` → `get_bid_path`), the same lookup every bid uses, so a
 * floor bid goes to the book the sale is on in THIS environment. A sale on the
 * Supabase book is not the engine's: the console says so and sends nothing
 * (two books for one lot can crown a winner who was outbid in the other).
 *
 * The lot list is live: the house's staff feed keeps each lot's current bid
 * current, and a lot that closes leaves the list.
 */
export function AdminAuctioneerConsole() {
  const { id: auctionId } = useParams<{ id: string }>();
  const { session, loading: sessionLoading } = useSession();
  const claims = readClaims(session);

  const [auction, setAuction] = useState<AuctionRow | null>(null);
  const [lots, setLots] = useState<LotRow[]>([]);
  const [bidders, setBidders] = useState<BidderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Form state
  const [selectedLotId, setSelectedLotId] = useState<string>('');
  const [bidderSearch, setBidderSearch] = useState('');
  const [selectedBidder, setSelectedBidder] = useState<BidderRow | null>(null);
  const [amountDollars, setAmountDollars] = useState('');
  const [paddleNumber, setPaddleNumber] = useState('');

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lastPlacedBid, setLastPlacedBid] = useState<{
    bid_id: string;
    amount_cents: number;
    lot_number: number;
    bidder: string;
    placed_at: string;
  } | null>(null);

  // Load auction + open lots + bidder list.
  const reload = useCallback(async () => {
    if (!auctionId) return;
    setLoading(true);
    setLoadError(null);

    const [auctionRes, lotsRes, statsRes] = await Promise.all([
      supabase
        .from('auctions')
        .select('id, tenant_id, title, status, timezone')
        .eq('id', auctionId)
        .maybeSingle(),
      supabase
        .from('lots')
        .select('id, lot_number, title, status, current_bid_cents, bid_count')
        .eq('auction_id', auctionId)
        .in('status', ['published', 'open', 'active'])
        .order('lot_number'),
      // bidder_lifetime_stats is the canonical tenant-scoped bidder view
      // (RLS handles the tenant filter). Only approved bidders can be paddled
      // on the floor — otherwise we'd be bypassing the approval flow.
      supabase
        .from('bidder_lifetime_stats')
        .select('user_id, tenant_bidder_number, status')
        .eq('status', 'approved')
        .order('tenant_bidder_number', { ascending: true, nullsFirst: false }),
    ]);

    if (auctionRes.error) {
      setLoadError(`auction: ${auctionRes.error.message}`);
    } else {
      setAuction((auctionRes.data ?? null) as AuctionRow | null);
    }
    if (lotsRes.error) {
      setLoadError((prev) => prev ?? `lots: ${lotsRes.error.message}`);
    } else {
      setLots((lotsRes.data ?? []) as LotRow[]);
    }
    if (statsRes.error) {
      setLoadError((prev) => prev ?? `bidders: ${statsRes.error.message}`);
      setBidders([]);
    } else {
      const statsRows = (statsRes.data ?? []) as Array<{
        user_id: string;
        tenant_bidder_number: number | null;
      }>;
      const userIds = [...new Set(statsRows.map((r) => r.user_id))];
      let profileMap = new Map<string, string | null>();
      if (userIds.length > 0) {
        const { data: profData, error: pErr } = await supabase
          .from('profiles')
          .select('id, display_name')
          .in('id', userIds);
        if (pErr) {
          setLoadError((prev) => prev ?? `profiles: ${pErr.message}`);
        } else {
          profileMap = new Map(
            ((profData ?? []) as Array<{ id: string; display_name: string | null }>).map((p) => [
              p.id,
              p.display_name,
            ]),
          );
        }
      }
      setBidders(
        statsRows.map((r) => ({
          user_id: r.user_id,
          display_name: profileMap.get(r.user_id) ?? null,
          tenant_bidder_number: r.tenant_bidder_number,
        })),
      );
    }

    setLoading(false);
  }, [auctionId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // ── live: the sale's route, and the staff feed for current bids ──
  const { source, failed: routeFailed } = useLiveSource(lots[0]?.id ?? null);
  const live = useSaleFeed(source, {
    summary: true,
    initialSaleState: feedSaleStateFromAuctionStatus(auction?.status),
  });
  const onRealtimeRow = useCallback((row: PublicLotRow) => {
    setLots((prev) =>
      prev.map((l) =>
        l.id === row.id
          ? {
              ...l,
              current_bid_cents: row.current_bid_cents,
              bid_count: row.bid_count,
              status: row.status,
            }
          : l,
      ),
    );
  }, []);
  useLotSubscription({
    target: { kind: 'auction', auctionId: auctionId ?? '' },
    onUpdate: onRealtimeRow,
    enabled: source?.kind === 'supabase' && auctionId !== undefined,
  });
  const liveFeed = live.feed;
  /** The lots still open for bidding now — a lot that closes leaves the list. */
  const openLots = useMemo(
    () =>
      lots.map((l) => withLiveFigures(l, liveFeed?.lot(l.id))).filter((l) => l.status === 'active'),
    // `live.version` re-reads the feed.
    [lots, liveFeed, live.version],
  );

  const filteredBidders = useMemo(() => {
    if (!bidderSearch.trim()) return bidders.slice(0, 50);
    const q = bidderSearch.toLowerCase();
    return bidders
      .filter(
        (b) =>
          (b.display_name ?? '').toLowerCase().includes(q) ||
          String(b.tenant_bidder_number ?? '').includes(q),
      )
      .slice(0, 50);
  }, [bidders, bidderSearch]);

  if (sessionLoading || loading) {
    return <main style={pageStyle}>Loading…</main>;
  }
  if (!session) return <Navigate to="/admin" replace />;
  if (!auctionId) return <Navigate to="/admin/auctions" replace />;
  if (loadError) {
    return (
      <main style={pageStyle}>
        <h1 style={titleStyle}>Auctioneer console</h1>
        <div style={alertStyle} role="alert">
          Failed to load: {loadError}
        </div>
      </main>
    );
  }
  if (!auction) {
    return (
      <main style={pageStyle}>
        <h1 style={titleStyle}>Auctioneer console</h1>
        <p style={{ color: 'var(--text-muted)' }}>Auction not found.</p>
      </main>
    );
  }

  async function handlePlaceBid(e: React.FormEvent) {
    e.preventDefault();
    if (!auction || !selectedLotId || !selectedBidder) return;
    // The book the sale is on decides where a bid may go — never a guess.
    if (source === null) {
      setSubmitError(
        routeFailed
          ? 'Could not look up where this sale’s bids go. Check the connection and try again.'
          : 'Still looking up where this sale’s bids go. Try again in a moment.',
      );
      return;
    }
    if (source.kind !== 'engine') {
      setSubmitError(
        'This sale’s bids are not on the auction engine, so the console cannot place floor bids for it.',
      );
      return;
    }

    const amountCents = Math.round(parseFloat(amountDollars) * 100);
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      setSubmitError('Enter a valid bid amount.');
      return;
    }
    if (!session) {
      setSubmitError('Session expired. Sign in again.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      // 1. Resolve the bidder's engine pseudonym for this tenant.
      const { data: pseudonym, error: pErr } = await supabase.rpc('engine_pseudonym_for_bidder', {
        p_tenant_id: auction.tenant_id,
        p_user_id: selectedBidder.user_id,
      });
      if (pErr) throw new Error(`pseudonym lookup: ${pErr.message}`);
      if (!pseudonym || typeof pseudonym !== 'string') {
        throw new Error(
          'No engine pseudonym registered for this bidder yet. Try again in a moment.',
        );
      }

      // 2. Build a fresh bid_id for chain idempotency.
      const bidId = `bid_floor_${crypto.randomUUID()}`;

      // 3. Call the engine's /v1/admin/floor-bid — at the address, and under
      //    the sale id, the bid path gave.
      const res = await fetch(`${source.engineUrl}/v1/admin/floor-bid`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auction_id: source.engineAuctionId,
          lot_id: selectedLotId,
          bid_id: bidId,
          amount_cents: amountCents,
          actor: { type: 'bidder', pseudonym_id: pseudonym },
          paddle_number: paddleNumber.trim() || undefined,
        }),
      });

      if (!res.ok) {
        const detail = await res.text();
        throw new Error(`engine ${res.status}: ${detail.slice(0, 300)}`);
      }

      // 4. Success — clear form, show confirmation, refresh lot list.
      const selectedLot = openLots.find((l) => l.id === selectedLotId);
      setLastPlacedBid({
        bid_id: bidId,
        amount_cents: amountCents,
        lot_number: selectedLot?.lot_number ?? 0,
        bidder:
          selectedBidder.display_name ?? `Bidder #${selectedBidder.tenant_bidder_number ?? '?'}`,
        placed_at: new Date().toISOString(),
      });
      setAmountDollars('');
      setPaddleNumber('');
      // Keep the lot + bidder selected — auctioneers often place multiple
      // bids in succession on the same lot.
      await reload();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  // Permission gate — only super_admin + tenant_admin (UI level; engine
  // enforces too on the floor-bid call).
  if (
    !claims.isSuperAdmin &&
    claims.appRole !== 'tenant_owner' &&
    claims.appRole !== 'tenant_staff'
  ) {
    return (
      <main style={pageStyle}>
        <h1 style={titleStyle}>Auctioneer console</h1>
        <p style={{ color: 'var(--text-muted)' }}>
          Only tenant_staff can use the auctioneer console.
        </p>
      </main>
    );
  }

  return (
    <main style={pageStyle}>
      <LiveStatusBanner status={live.status} timeZone={auction.timezone} audience="staff" />
      <nav style={{ marginBottom: '1rem' }}>
        <Link
          to={`/admin/auctions/${auctionId}`}
          style={{ color: 'var(--brand-accent)', fontSize: '0.875rem' }}
        >
          ← {auction.title}
        </Link>
      </nav>

      <header
        style={{
          marginBottom: '1.5rem',
          padding: '1.5rem 1.75rem',
          borderRadius: 'var(--r-card, 0.75rem)',
          background: 'var(--admin-wash, var(--surface-elev))',
          border: '1px solid var(--admin-hairline, var(--border))',
          boxShadow: 'var(--elev-1)',
        }}
      >
        <h1 style={titleStyle}>Auctioneer console</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', marginBottom: '0.6rem' }}>
          Place a bid on behalf of a phone, in-room, or walk-in bidder. Each bid records the floor
          bidder + the staff member who placed it; chain semantics are identical to external
          auctioneer software signed via HMAC.
        </p>
        <LearnMoreLink helpSlug="auctioneer-console" newTab>
          About the auctioneer console
        </LearnMoreLink>
      </header>

      {lastPlacedBid && (
        <div style={successStyle} role="status">
          ✓ Placed floor bid {dollars(lastPlacedBid.amount_cents)} on lot #
          {lastPlacedBid.lot_number} for {lastPlacedBid.bidder}.
        </div>
      )}

      {submitError && (
        <div role="alert" style={alertStyle}>
          {submitError}
        </div>
      )}

      <form
        onSubmit={(e) => {
          void handlePlaceBid(e);
        }}
        style={{ display: 'grid', gap: '1rem' }}
      >
        <Section title="Lot">
          {openLots.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>No open lots in this auction right now.</p>
          ) : (
            <Field label="Pick a lot">
              <select
                value={selectedLotId}
                onChange={(e) => setSelectedLotId(e.target.value)}
                required
                style={inputStyle}
              >
                <option value="">— Select —</option>
                {openLots.map((lot) => (
                  <option key={lot.id} value={lot.id}>
                    Lot #{lot.lot_number} — {lot.title} (current{' '}
                    {lot.current_bid_cents ? dollars(lot.current_bid_cents) : 'no bids'})
                  </option>
                ))}
              </select>
            </Field>
          )}
        </Section>

        <Section title="Bidder">
          <Field
            label="Search by name, email, or bidder number"
            hint="Pick from the list once you find the right bidder."
          >
            <input
              type="text"
              value={bidderSearch}
              onChange={(e) => {
                setBidderSearch(e.target.value);
                setSelectedBidder(null);
              }}
              placeholder="Type to filter…"
              style={inputStyle}
            />
          </Field>
          {bidderSearch.trim() && (
            <div
              style={{
                maxHeight: '12rem',
                overflowY: 'auto',
                border: '1px solid var(--admin-hairline, var(--border))',
                borderRadius: 'var(--r-tile, 0.375rem)',
                background: 'var(--surface)',
              }}
            >
              {filteredBidders.length === 0 ? (
                <p style={{ padding: '0.75rem', color: 'var(--text-muted)' }}>No matches.</p>
              ) : (
                filteredBidders.map((b) => (
                  <button
                    key={b.user_id}
                    type="button"
                    onClick={() => {
                      setSelectedBidder(b);
                      setBidderSearch(
                        `#${b.tenant_bidder_number ?? '?'} ${b.display_name ?? '(no name on file)'}`,
                      );
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '0.5rem 0.75rem',
                      borderBottom: '1px solid var(--admin-hairline, var(--border))',
                      background:
                        selectedBidder?.user_id === b.user_id
                          ? 'var(--surface-elev)'
                          : 'transparent',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>#{b.tenant_bidder_number ?? '?'}</span>{' '}
                    {b.display_name ?? (
                      <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        (no name on file)
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          )}
          {selectedBidder && (
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0 }}>
              Selected: <strong>#{selectedBidder.tenant_bidder_number ?? '?'}</strong>{' '}
              {selectedBidder.display_name ?? '(no name on file)'}
            </p>
          )}
        </Section>

        <Section title="Bid">
          <Field label="Amount (dollars)">
            <input
              type="number"
              min="0"
              step="0.01"
              value={amountDollars}
              onChange={(e) => setAmountDollars(e.target.value)}
              required
              placeholder="125.00"
              style={inputStyle}
              inputMode="decimal"
            />
          </Field>
          <Field label="Paddle / seat number (optional)">
            <input
              type="text"
              value={paddleNumber}
              onChange={(e) => setPaddleNumber(e.target.value)}
              placeholder="042"
              style={inputStyle}
              maxLength={32}
            />
          </Field>
        </Section>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <Button type="button" variant="outline" asChild>
            <Link to={`/admin/auctions/${auctionId}`}>Done</Link>
          </Button>
          <Button
            type="submit"
            disabled={submitting || !selectedLotId || !selectedBidder || !amountDollars}
          >
            {submitting ? 'Placing…' : 'Place floor bid'}
          </Button>
        </div>
      </form>
    </main>
  );
}

function dollars(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section
      style={{
        border: '1px solid var(--admin-hairline, var(--border))',
        borderRadius: 'var(--r-card, 0.5rem)',
        background: 'var(--surface-elev)',
        padding: '1rem 1.25rem',
      }}
    >
      <h2
        style={{
          fontSize: '0.875rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--text-muted)',
          marginBottom: '0.75rem',
        }}
      >
        {title}
      </h2>
      <div style={{ display: 'grid', gap: '0.75rem' }}>{children}</div>
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string | undefined;
  children: ReactNode;
}) {
  return (
    <label style={{ display: 'grid', gap: '0.25rem' }}>
      <span style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--text)' }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{hint}</span>}
    </label>
  );
}

const pageStyle: React.CSSProperties = {
  padding: '2rem 1.25rem',
  maxWidth: '48rem',
  margin: '0 auto',
};

const titleStyle: React.CSSProperties = {
  fontFamily: 'var(--font-heading-active)',
  fontSize: 'clamp(1.6rem, 1.2rem + 1.6vw, 2rem)',
  fontWeight: 700,
  letterSpacing: '-0.02em',
  color: 'var(--text)',
  marginBottom: '0.25rem',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.875rem',
};

const alertStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface-danger, #fef2f2)',
  color: 'var(--text-danger, #991b1b)',
  border: '1px solid var(--border-danger, #fecaca)',
  fontSize: '0.85rem',
  marginBottom: '1rem',
};

const successStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface-success, #f0fdf4)',
  color: 'var(--text-success, #166534)',
  border: '1px solid var(--border-success, #bbf7d0)',
  fontSize: '0.85rem',
  marginBottom: '1rem',
};
